import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { useCustomerContext } from "../../context/CustomerContext";
import { useSupplierContext } from "../../context/SupplierContext";
import { useReceiptVoucherContext } from "../../context/ReceiptVoucherContext";
import "./newReceiptVoucher.css";

const NewReceiptVoucher = () => {
  const navigate = useNavigate();
  const { customers, fetchCustomers } = useCustomerContext();
  const { suppliers, fetchSuppliers } = useSupplierContext();
  const { createReceiptVoucher, getCustomerInvoices, loading } =
    useReceiptVoucherContext();

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentType, setPaymentType] = useState("كاش");
  const [createdAt, setCreatedAt] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [notes, setNotes] = useState("");

  // بيانات الشيك
  const [checkNumber, setCheckNumber] = useState("");
  const [checkBank, setCheckBank] = useState("");
  const [checkDueDate, setCheckDueDate] = useState("");

  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchSuppliers();
  }, []);

  // عند اختيار زبون، جلب فواتيره
  const handleCustomerChange = async (customer) => {
    setSelectedCustomer(customer);
    setSelectedInvoice(null);
    setCustomerInvoices([]);
    setAmount("");

    if (customer) {
      setLoadingInvoices(true);
      try {
        const response = await getCustomerInvoices(
          customer.value,
          customer.party_type,
        );
        if (response.status === "success") {
          setCustomerInvoices(response.invoices);
        }
      } catch (err) {
        console.error("Error fetching invoices:", err);
        alert("حدث خطأ أثناء جلب فواتير الطرف");
      } finally {
        setLoadingInvoices(false);
      }
    }
  };

  // عند اختيار فاتورة، تعيين المبلغ المتبقي
  const handleInvoiceChange = (invoice) => {
    setSelectedInvoice(invoice);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCustomer) {
      alert("يرجى اختيار الزبون أو المورد");
      return;
    }

    if (!selectedInvoice) {
      alert("يرجى اختيار الفاتورة");
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      alert("يرجى إدخال مبلغ صحيح");
      return;
    }

    if (parseFloat(amount) > selectedInvoice.remaining_amount) {
      alert("المبلغ المدخل أكبر من المبلغ المتبقي للفاتورة");
      return;
    }

    if (paymentType === "شيك") {
      if (!checkNumber.trim()) {
        alert("يرجى إدخال رقم الشيك");
        return;
      }
      if (!checkBank.trim()) {
        alert("يرجى إدخال اسم البنك");
        return;
      }
      if (!checkDueDate) {
        alert("يرجى إدخال تاريخ استحقاق الشيك");
        return;
      }
    }

    const voucherData = {
      party_type: selectedCustomer.party_type,
      customer_id:
        selectedCustomer.party_type === "customer"
          ? selectedCustomer.value
          : null,
      supplier_id:
        selectedCustomer.party_type === "supplier"
          ? selectedCustomer.value
          : null,
      invoice_id: selectedInvoice.id,
      amount: parseFloat(amount),
      payment_type: paymentType,
      created_at: createdAt,
      notes: notes.trim() || null,
    };

    if (paymentType === "شيك") {
      voucherData.check_number = checkNumber.trim();
      voucherData.check_bank = checkBank.trim();
      voucherData.check_due_date = checkDueDate;
    }

    try {
      const response = await createReceiptVoucher(voucherData);
      if (response.status === "success") {
        alert("✅ تم إنشاء سند القبض بنجاح");
        // التحويل لصفحة السند
        navigate(`/receipt-voucher/${response.voucher_id}`);
      }
    } catch (err) {
      console.error("Error creating voresponseucher:", err);
      alert(err.response?.data?.message || "حدث خطأ أثناء إنشاء سند القبض");
    }
  };

  const customerOptions = customers.map((customer) => ({
    value: customer.id,
    label: `${customer.full_name} - ${customer.phone}`,
    party_type: "customer",
  }));
  const supplierOptions = suppliers.map((supplier) => ({
    value: supplier.id,
    label: `${supplier.full_name} - ${supplier.phone}`,
    party_type: "supplier",
  }));
  const partyOptions = [
    { label: "الزبائن", options: customerOptions },
    { label: "الموردون", options: supplierOptions },
  ];

  const invoiceOptions = customerInvoices.map((invoice) => ({
    ...invoice,
    value: invoice.id,
    label: `فاتورة #${
      invoice.id
    } - المبلغ الكلي: ${invoice.total_amount.toFixed(
      2
    )} ₪ - المدفوع: ${invoice.paid_amount.toFixed(
      2
    )} ₪ - المتبقي: ${invoice.remaining_amount.toFixed(2)} ₪`,
  }));

  return (
    <div className="new-receipt-voucher-page">
      <div className="receipt-voucher-container">
        <h1 className="page-title">إنشاء سند قبض جديد</h1>

        <form onSubmit={handleSubmit} className="voucher-form">
          {/* اختيار طرف سند القبض */}
          <div className="form-group">
            <label>
              الزبون أو المورد <span className="required">*</span>
            </label>
            <Select
              value={selectedCustomer}
              onChange={handleCustomerChange}
              options={partyOptions}
              getOptionValue={(option) =>
                `${option.party_type || "group"}-${option.value || option.label}`
              }
              placeholder="-- اختر الزبون أو المورد --"
              isSearchable
              isClearable
              noOptionsMessage={() => "لا توجد خيارات"}
              className="react-select-container"
              classNamePrefix="react-select"
              filterOption={(option, inputValue) => {
                if (!inputValue) return true;
                const searchWords = inputValue
                  .toLowerCase()
                  .split(" ")
                  .filter((w) => w);
                const optionText = option.label.toLowerCase();
                return searchWords.every((word) => optionText.includes(word));
              }}
            />
          </div>

          {/* اختيار الفاتورة */}
          {selectedCustomer && (
            <div className="form-group">
              <label>
                الفاتورة <span className="required">*</span>
              </label>
              {loadingInvoices ? (
                <p className="loading-text">جارٍ تحميل الفواتير...</p>
              ) : customerInvoices.length === 0 ? (
                <p className="no-invoices">
                  لا توجد فواتير بيع غير مدفوعة لهذا الطرف
                </p>
              ) : (
                <Select
                  value={selectedInvoice}
                  onChange={handleInvoiceChange}
                  options={invoiceOptions}
                  placeholder="-- اختر الفاتورة --"
                  isSearchable
                  isClearable
                  noOptionsMessage={() => "لا توجد خيارات"}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  filterOption={(option, inputValue) => {
                    if (!inputValue) return true;
                    const searchWords = inputValue
                      .toLowerCase()
                      .split(" ")
                      .filter((w) => w);
                    const optionText = option.label.toLowerCase();
                    return searchWords.every((word) =>
                      optionText.includes(word)
                    );
                  }}
                />
              )}
            </div>
          )}

          {/* المبلغ */}
          {selectedInvoice && (
            <>
              <div className="form-group">
                <label>
                  المبلغ المدفوع (₪) <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={amount}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, "");
                    setAmount(value);
                  }}
                  placeholder="أدخل المبلغ"
                  className="form-input"
                />
                <small className="help-text">
                  المبلغ المتبقي: {selectedInvoice.remaining_amount.toFixed(2)}{" "}
                  ₪
                </small>
              </div>

              {/* تاريخ السند */}
              <div className="form-group">
                <label>
                  تاريخ السند <span className="required">*</span>
                </label>
                <input
                  type="date"
                  value={createdAt}
                  onChange={(e) => setCreatedAt(e.target.value)}
                  className="form-input"
                />
              </div>

              {/* نوع الدفع */}
              <div className="form-group">
                <label>
                  نوع الدفع <span className="required">*</span>
                </label>
                <div className="payment-type-options">
                  <label className="radio-label">
                    <input
                      type="radio"
                      value="كاش"
                      checked={paymentType === "كاش"}
                      onChange={(e) => setPaymentType(e.target.value)}
                    />
                    <span>كاش</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      value="شيك"
                      checked={paymentType === "شيك"}
                      onChange={(e) => setPaymentType(e.target.value)}
                    />
                    <span>شيك</span>
                  </label>
                </div>
              </div>

              {/* بيانات الشيك */}
              {paymentType === "شيك" && (
                <div className="check-details">
                  <h3>بيانات الشيك</h3>
                  <div className="form-group">
                    <label>
                      رقم الشيك <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={checkNumber}
                      onChange={(e) => setCheckNumber(e.target.value)}
                      placeholder="أدخل رقم الشيك"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      اسم البنك <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={checkBank}
                      onChange={(e) => setCheckBank(e.target.value)}
                      placeholder="أدخل اسم البنك"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      تاريخ استحقاق الشيك <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      value={checkDueDate}
                      onChange={(e) => setCheckDueDate(e.target.value)}
                      className="form-input"
                    />
                  </div>
                </div>
              )}

              {/* الملاحظات */}
              <div className="form-group">
                <label>ملاحظات</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="أدخل ملاحظات إضافية (اختياري)"
                  className="form-textarea"
                  rows="4"
                />
              </div>

              {/* أزرار الإجراءات */}
              <div className="form-actions">
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? "جارٍ الحفظ..." : "حفظ سند القبض"}
                </button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => navigate(-1)}
                >
                  إلغاء
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default NewReceiptVoucher;
